import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/config";
import { matchRepository, leagueRepository } from "@/config/dataSource";
import { footballDataService } from "@/services/football/FootballDataService";
import type { LeagueSummary } from "@/modules/leagues/types/league";

export const matchesQueryKeys = {
  byRound: (leagueId: string, round?: number) => ["matches", leagueId, round ?? "current"] as const,
  rounds: (leagueId: string) => ["matches", leagueId, "rounds"] as const,
  participants: (leagueId: string, matchId: string) => ["matches", leagueId, matchId, "participants"] as const,
  liveState: (leagueId: string, matchId: string) => ["matches", leagueId, matchId, "liveState"] as const,
};

/**
 * Placar real já gravado pelo cron em `liveMatchState` (item pedido pelo
 * usuário) — usado SÓ como reforço de exibição pra partidas antigas cujo
 * `status` bruto da API ficou preso em "scheduled" (a mesma limitação de
 * dado desatualizado documentada em `isStaleScheduledPastKickoff`).
 * NUNCA faz uma chamada nova à API de futebol — é uma leitura simples de
 * um documento já existente no Firestore, gravado pelo cron toda vez que
 * ele confirma uma partida ao vivo/encerrada. Habilitar só quando
 * realmente precisar (partida "stale") evita leitura desnecessária nos
 * outros casos, onde o `match.status` cru já é suficiente.
 */
export function useLiveMatchState(leagueId: string | undefined, matchId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: matchesQueryKeys.liveState(leagueId ?? "unknown", matchId ?? "unknown"),
    queryFn: async () => {
      const snap = await getDoc(doc(db, "groups", leagueId as string, "liveMatchState", matchId as string));
      if (!snap.exists()) return null;
      const data = snap.data() as { home: number; away: number };
      return { home: data.home, away: data.away };
    },
    enabled: enabled && Boolean(leagueId) && Boolean(matchId),
    staleTime: 5 * 60_000, // 5min — dado histórico, não muda mais depois de gravado
  });
}

export function useMatchesByRound(leagueId: string | undefined, round?: number) {
  return useQuery({
    queryKey: matchesQueryKeys.byRound(leagueId ?? "unknown", round),
    queryFn: () => matchRepository.getByRound(leagueId as string, round),
    enabled: Boolean(leagueId),
    // Enquanto houver jogo ao vivo (ou prestes a começar) nesta rodada,
    // busca de novo a cada 30s pra placar/status ficarem realmente em
    // tempo real na tela — sem isso o usuário só via o placar atualizado
    // depois de sair e voltar pra tela.
    refetchInterval: (query) => {
      const matches = query.state.data;
      if (!matches) return false;
      const now = Date.now();
      const hasLiveOrImminent = matches.some((m) => {
        if (m.status === "live") return true;
        if (m.status !== "scheduled") return false;
        const hoursUntil = (new Date(m.kickoff).getTime() - now) / (60 * 60 * 1000);
        return hoursUntil <= 3; // inclui jogos que já deveriam ter começado
      });
      return hasLiveOrImminent ? 30_000 : false;
    },
  });
}

export function useAvailableRounds(leagueId: string | undefined) {
  return useQuery({
    queryKey: matchesQueryKeys.rounds(leagueId ?? "unknown"),
    queryFn: () => matchRepository.getAvailableRounds(leagueId as string),
    enabled: Boolean(leagueId),
  });
}

export function useParticipantPredictions(leagueId: string | undefined, matchId: string | undefined) {
  return useQuery({
    queryKey: matchesQueryKeys.participants(leagueId ?? "unknown", matchId ?? "unknown"),
    queryFn: () => matchRepository.getParticipantPredictions(leagueId as string, matchId as string),
    enabled: Boolean(leagueId) && Boolean(matchId),
    // Sem isto, a busca rodava só 1 vez e ficava CONGELADA no resultado
    // daquele instante — se pegasse vazio (ex: bem no momento em que o
    // jogo estava começando), nunca mais tentava de novo sozinha, mesmo
    // com os palpites já disponíveis segundos depois. Mesmo ritmo já
    // usado em toda parte "ao vivo" do app (30s).
    refetchInterval: 30_000,
  });
}

/** Uma partida específica — usado pela tela de Detalhes da partida. */
export function useMatch(leagueId: string | undefined, matchId: string | undefined) {
  return useQuery({
    queryKey: ["matches", leagueId ?? "unknown", "single", matchId ?? "unknown"],
    queryFn: () => matchRepository.getById(leagueId as string, matchId as string),
    enabled: Boolean(leagueId) && Boolean(matchId),
    // Mesmo critério já usado em useMatchesByRound — nunca parar de tentar
    // só porque o status NUNCA chegou a ser visto como "live" por essa
    // tela. Bug real corrigido: se a API demora e o status pula direto de
    // "scheduled" pra "finished" (sem passar por "live" no meio, do jeito
    // que essa tela via), a atualização automática parava de tentar e a
    // tela ficava congelada mostrando "Agendada" pra sempre — mesmo com a
    // partida já encerrada e os palpites de todos já disponíveis.
    refetchInterval: (query) => {
      const match = query.state.data;
      if (!match) return false;
      if (match.status === "live") return true && 30_000;
      if (match.status !== "scheduled") return false; // finished/postponed = já é estado final, não precisa mais tentar
      const hoursUntil = (new Date(match.kickoff).getTime() - Date.now()) / (60 * 60 * 1000);
      return hoursUntil <= 3 ? 30_000 : false; // inclui negativo = já deveria ter começado
    },
  });
}

/** Últimos resultados (forma recente) de um time — item "Últimos
 * resultados" da tela de Detalhes da partida. Puramente decorativo/
 * informativo — nunca usado em cálculo de pontuação. */
export function useTeamRecentForm(teamId: string | undefined) {
  return useQuery({
    queryKey: ["team-form", teamId ?? "unknown"],
    queryFn: () => footballDataService.getTeamRecentForm(teamId as string),
    enabled: Boolean(teamId),
    staleTime: 6 * 60 * 60 * 1000,
  });
}

export function useSavePrediction(leagueId: string | undefined, round?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, homeScore, awayScore }: { matchId: string; homeScore: number; awayScore: number }) => {
      if (!leagueId) throw new Error("Liga inválida.");
      return matchRepository.savePrediction(leagueId, matchId, homeScore, awayScore);
    },
    onSuccess: () => {
      if (leagueId) {
        queryClient.invalidateQueries({ queryKey: matchesQueryKeys.byRound(leagueId, round) });
      }
    },
  });
}

/**
 * Próximos jogos de TODAS as ligas do usuário logado, agregados numa lista
 * só, ordenados por data/horário/competição (aba "Jogos" — antes era
 * "Em breve"). Cada item já carrega o leagueId de origem, então o
 * MatchCard funciona normalmente (palpitar entra na liga certa).
 */
export function useUpcomingMatchesAcrossLeagues(leagues: LeagueSummary[] | undefined) {
  return useQuery({
    queryKey: ["matches", "upcoming-all", (leagues ?? []).map((l) => l.id).sort().join(",")],
    queryFn: async () => {
      // Promise.all (versão anterior) falhava a lista INTEIRA se UMA liga
      // desse erro (ex: 429 de rate-limit da Football Data API) — sintoma
      // real relatado: "às vezes carrega, às vezes não" nesta tela. Com
      // allSettled, uma liga com problema momentâneo só fica de fora da
      // lista agregada; as outras continuam aparecendo normalmente.
      const [listResults, detailResults] = await Promise.all([
        Promise.allSettled((leagues ?? []).map((l) => matchRepository.getByRound(l.id))),
        Promise.allSettled((leagues ?? []).map((l) => leagueRepository.getLeagueById(l.id))),
      ]);
      const lists = listResults.map((r) => (r.status === "fulfilled" ? r.value : []));
      const details = detailResults.map((r) => (r.status === "fulfilled" ? r.value : null));
      for (const r of [...listResults, ...detailResults]) {
        if (r.status === "rejected") {
          console.warn("[useUpcomingMatchesAcrossLeagues] uma liga falhou ao carregar, seguindo com as demais:", r.reason);
        }
      }

      const leagueNameByLeagueId = new Map((leagues ?? []).map((l) => [l.id, l.competitionName]));
      const detailByLeagueId = new Map((details.filter(Boolean) as NonNullable<(typeof details)[number]>[]).map((d) => [d.id, d]));
      const flat = lists.flat().map((m) => {
        const detail = detailByLeagueId.get(m.leagueId);
        return {
          ...m,
          competitionLabel: leagueNameByLeagueId.get(m.leagueId) ?? m.competitionId,
          scoringRules: detail?.rules.rules,
          totalParticipants: detail?.participantsCount,
        };
      });
      return flat.sort((a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime());
    },
    enabled: Boolean(leagues) && leagues!.length > 0,
    refetchInterval: 60_000,
  });
}
